import React, { useState } from 'react';
import { Gauge, Settings2, CircleDot, Fuel, Car, Palette, Share2, Check, Tag } from 'lucide-react';
import { fmtPrice, fmtKm, transLabel, driveLabel, fuelLabel, bodyLabel } from './inventoryUtils';

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '-');

const Tile = ({ icon: Icon, label, value }) => (
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-2.5 sm:p-3 rounded-xl border border-gray-100 hover:shadow-md transition-shadow">
    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#1a5c3b]/10 rounded-lg flex items-center justify-center mb-1.5 sm:mb-2"><Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#1a5c3b]" /></div>
    <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="font-bold text-gray-900 text-xs sm:text-sm">{value}</p>
  </div>
);

export const VehicleInfoCard = ({ v, en, onApply }) => {
  const [copied, setCopied] = useState(false);
  const price = fmtPrice(v.price, en);
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: `${v.year} ${v.make} ${v.model}`, url }).catch(() => {}); return; }
    await navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const tiles = [
    [Gauge, en ? 'Mileage' : 'Kilométrage', fmtKm(v.mileage, en)],
    [Settings2, 'Transmission', transLabel(v.transmission, en)],
    [CircleDot, en ? 'Drivetrain' : 'Traction', driveLabel(v.drivetrain, en)],
    [Fuel, en ? 'Fuel' : 'Carburant', fuelLabel(v.fuel_type, en)],
    v.engine ? [Fuel, en ? 'Engine' : 'Moteur', v.engine] : null,
    [Car, en ? 'Body style' : 'Carrosserie', bodyLabel(v.body_type, en)],
    [Palette, en ? 'Exterior' : 'Extérieur', cap(v.exterior_color)],
    [Palette, en ? 'Interior' : 'Intérieur', cap(v.interior_color)],
  ].filter(Boolean);

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-8 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <span className="inline-block bg-[#1a5c3b]/10 text-[#1a5c3b] text-sm font-semibold px-3 py-1 rounded-full mb-2">{v.year}</span>
          <h1 className="font-black text-2xl lg:text-3xl text-gray-900 uppercase" data-testid="vehicle-title">{v.make} {v.model} {v.trim && <span className="font-bold">{v.trim}</span>}</h1>
          {v.title && <p className="text-sm text-gray-500 font-medium mt-1">{v.title}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {v.stock_number && <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-lg"><Tag className="w-3.5 h-3.5 text-gray-400" />Stock #{v.stock_number}</span>}
            {v.vin && <span className="inline-flex items-center bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-lg font-mono">{en ? 'VIN' : 'NIV'}: {v.vin}</span>}
            <button type="button" onClick={share} className="inline-flex items-center gap-1.5 bg-[#1a5c3b]/10 text-[#1a5c3b] text-xs px-2.5 py-1 rounded-lg hover:bg-[#1a5c3b]/20 transition-colors" data-testid="share-vehicle">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}{copied ? (en ? 'Copied' : 'Copié') : (en ? 'Share' : 'Partager')}
            </button>
          </div>
        </div>
        <div className="sm:text-right shrink-0">
          <p className="font-black text-3xl lg:text-4xl text-[#1a5c3b] tracking-tight whitespace-nowrap" data-testid="vehicle-price">{price ? `*${price}` : (en ? 'Call us' : 'Appelez-nous')}</p>
          <button type="button" onClick={onApply} className="mt-3 inline-flex items-center justify-center bg-[#1a5c3b] hover:bg-[#165432] text-white rounded-full text-base h-11 px-7 w-full sm:w-auto font-bold transition-colors" data-testid="vehicle-financing-btn">
            {en ? 'Finance Me!' : 'Financez-moi!'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {tiles.map(([Icon, l, val]) => <Tile key={l} icon={Icon} label={l} value={val} />)}
      </div>
    </div>
  );
};
